import React, { useState, useEffect, useRef } from 'react';
import Clarity from '@microsoft/clarity';
import './BookingModule.css';

interface Package {
  id: string;
  title: string;
  price: string;
  rentalDays: number;
  isPopular?: boolean;
}

interface BookingModuleProps {
  selectedPackageId?: string | null;
}

const packages: Package[] = [
  {
    id: 'moments',
    title: 'The Milestone Moment',
    price: '$249',
    rentalDays: 3
  },
  {
    id: 'memories',
    title: 'The First Chapter',
    price: '$349',
    rentalDays: 7,
    isPopular: true
  },
  {
    id: 'legacy',
    title: 'The Whole Story',
    price: '$999',
    rentalDays: 30
  }
];

const BookingModule: React.FC<BookingModuleProps> = ({ selectedPackageId }) => {
  const [currentDate, setCurrentDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [showReserveButton, setShowReserveButton] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBookingMessage, setShowBookingMessage] = useState(false);
  const [showEmailSignup, setShowEmailSignup] = useState(false);
  const [formData, setFormData] = useState({
    zipCode: '',
    email: ''
  });

  // Refs for smooth scrolling
  const calendarRef = useRef<HTMLDivElement>(null);
  const reserveSectionRef = useRef<HTMLDivElement>(null);
  const bookingFormRef = useRef<HTMLDivElement>(null);

  // Smooth scroll helper function
  const scrollToElement = (elementRef: React.RefObject<HTMLDivElement | null>, offset: number = 100) => {
    if (elementRef.current) {
      const element = elementRef.current;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (selectedPackageId) {
      const packageToSelect = packages.find(pkg => pkg.id === selectedPackageId);
      if (packageToSelect) {
        setSelectedPackage(packageToSelect);
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setShowReserveButton(false);
        setShowForm(false);
        setShowBookingMessage(false);
        setShowEmailSignup(false);
      }
    }
  }, [selectedPackageId]);

  const unavailableDates = [
    '2024-08-19',
    '2024-08-20',
    '2024-08-26',
    '2024-09-09',
    '2024-09-23',
    '2024-10-07'
  ];

  const isDateDisabled = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const twoDaysFromNow = new Date(today);
    twoDaysFromNow.setDate(today.getDate() + 2);
    
    return date < twoDaysFromNow;
  };

  const isDateBooked = (date: Date): boolean => {
    const dateStr = date.toISOString().split('T')[0];
    return unavailableDates.includes(dateStr);
  };

  const calculateEndDate = (startDate: Date, rentalDays: number): Date => {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + rentalDays - 1);
    return endDate;
  };

  const isDateInRange = (date: Date, startDate: Date, endDate: Date): boolean => {
    return date >= startDate && date <= endDate;
  };

  const isRangeAvailable = (startDate: Date, rentalDays: number): boolean => {
    for (let i = 0; i < rentalDays; i++) {
      const checkDate = new Date(startDate);
      checkDate.setDate(startDate.getDate() + i);
      if (isDateDisabled(checkDate) || isDateBooked(checkDate)) {
        return false;
      }
    }
    return true;
  };

  const handlePackageSelect = (pkg: Package) => {
    Clarity.event('booking_package_select');
    setSelectedPackage(pkg);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setShowReserveButton(false);
    setShowForm(false);
    setShowBookingMessage(false);
    setShowEmailSignup(false);
    
    // Scroll to calendar after a short delay to allow rendering
    setTimeout(() => {
      scrollToElement(calendarRef, 80);
    }, 100);
  };

  const handleDateClick = (date: Date) => {
    if (!selectedPackage || isDateDisabled(date) || isDateBooked(date)) {
      return;
    }
    
    if (!isRangeAvailable(date, selectedPackage.rentalDays)) {
      return;
    }
    
    Clarity.event('booking_date_select');
    
    const endDate = calculateEndDate(date, selectedPackage.rentalDays);
    setSelectedStartDate(date);
    setSelectedEndDate(endDate);
    setShowReserveButton(true);
    setShowForm(false);
    setShowBookingMessage(false);
    setShowEmailSignup(false);
    
    // Scroll to reserve section after a short delay to allow rendering
    setTimeout(() => {
      scrollToElement(reserveSectionRef, 80);
    }, 100);
  };

  const handleReserveClick = () => {
    Clarity.event('booking_reserve_package');
    setShowForm(true);
    
    // Scroll to bottom of page after a short delay to allow rendering
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 200);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    Clarity.event('booking_form_submit');
    console.log('Form submitted:', { selectedPackage, selectedStartDate, selectedEndDate, formData });
    setShowBookingMessage(true);
    setShowEmailSignup(true);
    
    // Scroll to bottom of page after a short delay to show the message
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 200);
  };

  const handleEmailSignup = (e: React.FormEvent) => {
    e.preventDefault();
    Clarity.event('email_signup');
    console.log('Email signup:', { email: formData.email, zipCode: formData.zipCode });
    alert('Thank you! We\'ll keep you updated on our expansion to your area.');
    setFormData({ ...formData, email: '' });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startCalendar = new Date(firstDay);
    const startDay = firstDay.getDay();
    startCalendar.setDate(1 - (startDay === 0 ? 6 : startDay - 1));
    
    const endCalendar = new Date(lastDay);
    const endDay = lastDay.getDay();
    endCalendar.setDate(lastDay.getDate() + (endDay === 0 ? 0 : 7 - endDay));
    
    const weeks = [];
    const current = new Date(startCalendar);
    
    while (current <= endCalendar) {
      const weekDates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(current);
        date.setDate(current.getDate() + i);
        weekDates.push(date);
      }
      weeks.push(weekDates);
      current.setDate(current.getDate() + 7);
    }
    
    return weeks;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateRange = (startDate: Date, endDate: Date): string => {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const getDurationText = (days: number): string => {
    if (days === 30) return '1 month';
    if (days === 7) return '1 week';
    return `${days} days`;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const goToPreviousMonth = () => {
    Clarity.event('calendar_previous_month');
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    Clarity.event('calendar_next_month');
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  return (
    <div className="booking-module">
      <div className="booking-header">
      </div>

      {/* Package Selection */}
      <div className="package-selection">
        <h4>Choose Your Package</h4>
        <div className="packages-grid">
          {packages.map((pkg) => (
            <button 
              key={pkg.id} 
              className={`package-btn ${selectedPackage?.id === pkg.id ? 'selected' : ''} ${pkg.isPopular ? 'popular' : ''}`}
              onClick={() => handlePackageSelect(pkg)}
            >
              <span className="package-title">{pkg.title}</span>
              <span className="package-duration">{getDurationText(pkg.rentalDays)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Calendar - only show when package is selected */}
      {selectedPackage && (
        <div className="calendar-container" ref={calendarRef}>
          <div className="calendar-header">
            <button 
              className="nav-button" 
              onClick={goToPreviousMonth}
              aria-label="Previous month"
            >
              ←
            </button>
            <h4>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h4>
            <button 
              className="nav-button" 
              onClick={goToNextMonth}
              aria-label="Next month"
            >
              →
            </button>
          </div>
          
          <div className="calendar-instruction">
            <p>Select your preferred start date for {getDurationText(selectedPackage.rentalDays)} rental</p>
          </div>
          
          <div className="calendar-grid">
            <div className="weekdays">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="weekday">{day}</div>
              ))}
            </div>
            
            <div className="days-grid">
              {renderCalendar().map((week, weekIndex) => (
                <div key={weekIndex} className="week-row">
                  {week.map((date, dateIndex) => {
                    const isOtherMonth = date.getMonth() !== currentDate.getMonth();
                    const isDisabled = isDateDisabled(date);
                    const isBooked = isDateBooked(date);
                    const isSelected = selectedStartDate && selectedEndDate && isDateInRange(date, selectedStartDate, selectedEndDate);
                    const isStartDate = selectedStartDate && date.toDateString() === selectedStartDate.toDateString();
                    const isEndDate = selectedEndDate && date.toDateString() === selectedEndDate.toDateString();
                    const canStartRange = !isDisabled && !isBooked && !isOtherMonth && isRangeAvailable(date, selectedPackage?.rentalDays || 1);
                    
                    return (
                      <div 
                        key={dateIndex} 
                        className={`day ${isOtherMonth ? 'other-month' : ''} ${isDisabled ? 'is-disabled' : ''} ${isBooked ? 'is-booked' : ''} ${isSelected ? 'is-selected' : ''} ${isStartDate ? 'is-start' : ''} ${isEndDate ? 'is-end' : ''} ${!canStartRange && !isOtherMonth ? 'unavailable-range' : ''}`}
                        onClick={() => handleDateClick(date)}
                      >
                        {date.getDate()}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {showReserveButton && selectedStartDate && selectedEndDate && selectedPackage && !showForm && (
        <div className="reserve-section" ref={reserveSectionRef}>
          <div className="selected-dates-summary">
            <h4>Selected Dates</h4>
            <div className="summary-details">
              <div className="summary-row">
                <span className="label">Package:</span>
                <span className="value">{selectedPackage.title}</span>
              </div>
              <div className="summary-row">
                <span className="label">Duration:</span>
                <span className="value">{getDurationText(selectedPackage.rentalDays)}</span>
              </div>
              <div className="summary-row">
                <span className="label">Rental Period:</span>
                <span className="value">{formatDateRange(selectedStartDate, selectedEndDate)}</span>
              </div>
              <div className="summary-row">
                <span className="label">Price:</span>
                <span className="value">{selectedPackage.price}</span>
              </div>
            </div>
          </div>
          <button className="reserve-button" onClick={handleReserveClick}>
            Reserve This Package
          </button>
        </div>
      )}
      
      {showForm && selectedStartDate && selectedEndDate && selectedPackage && (
        <div className="booking-form-container" ref={bookingFormRef}>
          <div className="booking-summary">
            <h4>Booking Summary</h4>
            <div className="summary-details">
              <div className="summary-row">
                <span className="label">Package:</span>
                <span className="value">{selectedPackage.title}</span>
              </div>
              <div className="summary-row">
                <span className="label">Duration:</span>
                <span className="value">{getDurationText(selectedPackage.rentalDays)}</span>
              </div>
              <div className="summary-row">
                <span className="label">Rental Period:</span>
                <span className="value">{formatDateRange(selectedStartDate, selectedEndDate)}</span>
              </div>
              <div className="summary-row">
                <span className="label">Price:</span>
                <span className="value">{selectedPackage.price}</span>
              </div>
            </div>
          </div>
          
          <form className="booking-form" onSubmit={handleFormSubmit}>
            <div className="form-group">
              <label htmlFor="zipCode">ZIP Code *</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleFormChange}
                placeholder="12345"
                required
              />
            </div>
            
            {showBookingMessage && (
              <div className="booking-message">
                <p>The magic is coming soon! Your area is on our expansion map. Join our community to receive updates and be the first to know when booking opens in your neighborhood.</p>
              </div>
            )}
            
            {showEmailSignup && (
              <form className="email-signup-form" onSubmit={handleEmailSignup}>
                <div className="email-input-group">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="Enter your email address"
                    required
                    className="email-input"
                  />
                  <button type="submit" className="keep-updated-button">
                    Keep Me Updated
                  </button>
                </div>
              </form>
            )}
            
            <button type="submit" className="submit-button">
              Request Booking - {selectedPackage.price}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default BookingModule;